// Matching a remittance to the claim lines it pays.
//
// Two paths arrive here and must agree: the 835 once ERA delivery is enrolled,
// and the GAMMIS portal PDF that `parse-ra` reads today. A payment posted one
// way by one path and another way by the other is how a billing record stops
// being trustworthy, so the decision lives in one tested function and the
// callers only do the writing.
//
// Matching is tried in order of how much it can be trusted:
//
//   1. CLP01 — the value we put in CLM01 when the claim was built, which is
//      the claim_lines id. An exact lookup, available only for claims CAERA
//      filed itself.
//   2. ICN — exact, but only once a line already carries one.
//   3. Medicaid ID + date of service + procedure code. This is a guess. It is
//      what the old importer used for everything, and it is wrong whenever a
//      client has two lines of the same code on the same day — which happens.
//      Matches made this way are flagged so a person can look.

import { parseEobsCodes } from './eobCodes.js';

const day = (v) => (typeof v === 'string' && v.length >= 10 ? v.slice(0, 10) : v || null);
const norm = (v) => String(v ?? '').trim().toUpperCase();
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * The adjustments on a claim, header and service lines together.
 * The group matters: CO is written off, PR is the patient's, OA another payer's.
 */
export const allAdjustments = (claim) => [
  ...(claim.adjustments || []),
  ...(claim.serviceLines || []).flatMap((l) => l.adjustments || []),
];

/** "CO:45 CO:16" — the same shape parse-ra already stores in eobs_codes. */
export const adjustmentCodes = (claim) =>
  allAdjustments(claim).map((a) => `${a.group}:${a.code}`).filter(Boolean).join(' ') || null;

/**
 * Normalise a claim as `parse-ra` reads it off a GAMMIS portal PDF.
 *
 * The PDF and the 835 describe the same event in different shapes. The PDF
 * states the allowed amount outright and flattens every code into one string
 * ("9918 CO:45 572.16- 0351 CO:16"); the 835 itemises adjustments and leaves
 * the allowed amount to be derived. Both end up here so one function decides
 * what a payment means.
 */
export const claimFromRaRow = (row, { checkDate = null, checkNumber = null } = {}) => {
  const codes = parseEobsCodes(row?.eobs_codes);
  return {
    // The PDF carries no CLM01 — these claims were filed by hand in GAMMIS,
    // long before CAERA put a control number on anything.
    claimControlNumber: '',
    statusCode: null,
    status: row?.status === 'denied' ? 'denied' : (Number(row?.paid) || 0) > 0 ? 'paid' : 'denied',
    billed: Number(row?.billed) || 0,
    paid: Number(row?.paid) || 0,
    // Stated by the remittance rather than derived, so it is preferred over
    // the CO-adjustment arithmetic below.
    allowedAmount: row?.allowed != null ? Number(row.allowed) : null,
    icn: row?.icn || '',
    medicaidId: row?.medicaid_id || '',
    patientName: row?.client_name || '',
    serviceLines: [{
      serviceCode: row?.service_code || '',
      modifier: '',
      billed: Number(row?.billed) || 0,
      paid: Number(row?.paid) || 0,
      units: 0,
      fromDate: day(row?.service_date),
      toDate: day(row?.service_date),
      adjustments: [],
    }],
    adjustments: codes
      .filter((t) => t.kind === 'carc')
      .map((t) => ({ group: t.group, code: t.code, amount: 0 })),
    rawCodes: String(row?.eobs_codes || '').trim() || null,
    checkDate,
    checkNumber,
  };
};

/**
 * What this remittance says should change on a claim line.
 *
 * Allowed amount is billed less the contractual write-off, which is the figure
 * that should equal `accrued_amount` — on AHFC's remittances it does, to the
 * cent, because the charge is 2.5x and CO:45 removes the difference. Where the
 * source states the allowed amount itself, that is used instead of deriving it.
 */
export const updateFromClaim = (claim) => {
  const adjustments = allAdjustments(claim);
  const contractual = adjustments.filter((a) => a.group === 'CO').reduce((s, a) => s + a.amount, 0);
  const denial = adjustments.find((a) => ['CO', 'OA', 'PI'].includes(a.group));

  const allowed = claim.allowedAmount != null
    ? round2(claim.allowedAmount)
    : claim.billed > 0 ? round2(claim.billed - contractual) : null;

  return {
    paid_amount: round2(claim.paid),
    paid_date: claim.checkDate || null,
    status: claim.status,
    allowed_amount: allowed,
    // The flat code string where the source had one, otherwise rebuilt from
    // the itemised adjustments.
    eobs_codes: claim.rawCodes || adjustmentCodes(claim),
    // A denial code is the CARC, not the whole EOB string. The RA importer
    // used to store the entire string here, which is why Claims.js had to
    // split it back apart on the way out.
    denial_code: claim.status === 'denied' ? (denial?.code || null) : null,
    ...(claim.icn ? { icn: claim.icn } : {}),
  };
};

/**
 * Pair remittance claims with claim lines.
 *
 * Returns matched pairs carrying the update to apply and how the match was
 * made, plus the claims nothing could be found for. Nothing is written here.
 */
export const matchRemittance = ({ claims = [], lines = [] } = {}) => {
  const byId  = new Map(lines.map((l) => [norm(String(l.id).replace(/-/g, '')), l]));
  const byIcn = new Map(lines.filter((l) => l.icn).map((l) => [norm(l.icn), l]));
  const taken = new Set();

  const matched = [];
  const unmatched = [];

  const claim = (c) => {
    const control = norm(c.claimControlNumber);
    if (control && byId.has(control) && !taken.has(byId.get(control).id)) {
      return { line: byId.get(control), how: 'claim_control_number' };
    }
    const icn = norm(c.icn);
    if (icn && byIcn.has(icn) && !taken.has(byIcn.get(icn).id)) {
      return { line: byIcn.get(icn), how: 'icn' };
    }
    const svc = (c.serviceLines || [])[0];
    const dos = day(svc?.fromDate);
    const guess = lines.find((l) =>
      !taken.has(l.id) &&
      norm(l.medicaid_id) === norm(c.medicaidId) && norm(c.medicaidId) !== '' &&
      (dos ? day(l.service_date) === dos : true) &&
      (svc?.serviceCode ? norm(l.service_code) === norm(svc.serviceCode) : true));
    return guess ? { line: guess, how: 'heuristic' } : null;
  };

  for (const c of claims) {
    const hit = claim(c);
    if (!hit) { unmatched.push(c); continue; }
    taken.add(hit.line.id);
    matched.push({ line: hit.line, claim: c, how: hit.how, update: updateFromClaim(c) });
  }

  return {
    matched,
    unmatched,
    // Anything matched by guesswork is worth a person's eye before it is
    // treated as settled.
    uncertain: matched.filter((m) => m.how === 'heuristic'),
  };
};

/** Total actually paid by the matched claims. */
export const totalPaid = (matched = []) =>
  round2(matched.reduce((s, m) => s + (m.update.paid_amount || 0), 0));

/**
 * What a run has been paid, from its lines. The run's total used to be set to
 * the remittance just posted, so a run paid across two remittances showed only
 * the second, and a payment corrected by hand on a line never reached it.
 * Summing the lines is the only figure that cannot drift from them.
 */
export const runPaidFromLines = (lines = []) =>
  round2((lines || []).reduce((s, l) => s + (Number(l.paid_amount) || 0), 0));

/** Is every line on the run now settled? */
export const runIsSettled = (lines = [], matched = []) => {
  const done = new Set(matched.map((m) => m.line.id));
  return lines.every((l) => done.has(l.id) || ['paid', 'denied', 'voided'].includes(l.status));
};
